import { toast } from 'react-hot-toast';

/**
 * Centralized error handling utility for the application
 * Provides consistent error logging, user notifications, and error categorization
 */

export enum ErrorCategory {
  AUTHENTICATION = 'authentication',
  AUTH = 'auth',  // Adding AUTH for backward compatibility
  NETWORK = 'network',
  VALIDATION = 'validation',
  API = 'api',
  AI = 'ai',  // Adding AI category for AI-related errors
  STORAGE = 'storage',
  PERMISSION = 'permission',
  NOT_FOUND = 'not_found', // Adding NOT_FOUND for resource not found errors
  UNKNOWN = 'unknown'
}

export interface AppError extends Error {
  category: ErrorCategory;
  statusCode?: number;
  userMessage?: string;
  context?: Record<string, any>;
}

/**
 * Create a standardized application error
 */
export function createAppError(
  message: string,
  category: ErrorCategory = ErrorCategory.UNKNOWN,
  statusCode?: number,
  userMessage?: string,
  context?: Record<string, any>
): AppError {
  const error = new Error(message) as AppError;
  error.category = category;
  error.statusCode = statusCode;
  error.userMessage = userMessage;
  error.context = context;
  return error;
}

/**
 * Enhanced error logging with context and categorization
 */
export function logError(error: Error | AppError, context?: Record<string, any>): void {
  const timestamp = new Date().toISOString();
  const appError = error as AppError;
  
  const logData = {
    timestamp,
    message: error.message,
    category: appError.category || ErrorCategory.UNKNOWN,
    statusCode: appError.statusCode,
    context: { ...appError.context, ...context },
    stack: error.stack
  };

  console.error('Application Error:', logData);
  
  // In production, you might want to send this to an error monitoring service
  // Example: Sentry, LogRocket, etc.
  // errorMonitoringService.captureError(logData);
}

/**
 * Display user-friendly error notifications
 */
export function showErrorToUser(error: Error | AppError, fallbackMessage?: string): void {
  const appError = error as AppError;
  const userMessage = appError.userMessage || fallbackMessage || getGenericErrorMessage(appError.category);
  
  toast.error(userMessage, {
    duration: 5000,
    position: 'top-right'
  });
}

/**
 * Get generic error messages based on category
 */
function getGenericErrorMessage(category?: ErrorCategory): string {
  switch (category) {
    case ErrorCategory.AUTHENTICATION:
    case ErrorCategory.AUTH:
      return 'Authentication failed. Please log in again.';
    case ErrorCategory.NETWORK:
      return 'Network error. Please check your connection and try again.';
    case ErrorCategory.VALIDATION:
      return 'Please check your input and try again.';
    case ErrorCategory.API:
      return 'Service temporarily unavailable. Please try again later.';
    case ErrorCategory.AI:
      return 'AI service error. Please try again with different input.';
    case ErrorCategory.STORAGE:
      return 'Unable to save your data. Please try again.';
    case ErrorCategory.PERMISSION:
      return 'You do not have permission to perform this action.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}

/**
 * Handle HTTP response errors with proper categorization
 */
export function handleHttpError(response: Response, responseData?: any): AppError {
  let category: ErrorCategory;
  let userMessage: string;

  switch (response.status) {
    case 400:
      category = ErrorCategory.VALIDATION;
      userMessage = 'Invalid request. Please check your input.';
      break;
    case 401:
      category = ErrorCategory.AUTHENTICATION;
      userMessage = 'You need to log in to perform this action.';
      break;
    case 403:
      category = ErrorCategory.PERMISSION;
      userMessage = 'You do not have permission to perform this action.';
      break;
    case 404:
      category = ErrorCategory.API;
      userMessage = 'The requested resource was not found.';
      break;
    case 429:
      category = ErrorCategory.API;
      userMessage = 'Too many requests. Please wait a moment and try again.';
      break;
    case 500:
    case 502:
    case 503:
    case 504:
      category = ErrorCategory.API;
      userMessage = 'Server error. Please try again later.';
      break;
    default:
      category = ErrorCategory.NETWORK;
      userMessage = 'Network error. Please check your connection.';
  }

  const errorMessage = responseData?.error || responseData?.message || `HTTP ${response.status}: ${response.statusText}`;
  
  return createAppError(
    errorMessage,
    category,
    response.status,
    userMessage,
    { 
      url: response.url,
      responseData: responseData ? JSON.stringify(responseData) : undefined
    }
  );
}

/**
 * Handle and process service errors with logging and user notification
 */
export function handleServiceError(
  error: Error | AppError,
  context?: Record<string, any>,
  showToUser: boolean = true
): void {
  // Log the error with context
  logError(error, context);
  
  // Show user-friendly notification if requested
  if (showToUser) {
    showErrorToUser(error);
  }
}

/**
 * Async wrapper that provides centralized error handling for promises
 */
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  context?: Record<string, any>,
  showToUser: boolean = true
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    handleServiceError(error as Error, context, showToUser);
    throw error;
  }
}

/**
 * Retry logic for failed operations
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000,
  context?: Record<string, any>
): Promise<T> {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      
      if (attempt === maxRetries) {
        logError(lastError, { ...context, totalAttempts: attempt });
        throw lastError;
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }
  
  throw lastError!;
}
